/**
 * Pure grid pathfinding helpers (no Phaser). Used by Actor.walkTo and unit-tested in Node.
 *
 * Coordinates: tile space for BFS (integers), world pixels for line sampling.
 */
export interface GridPoint { x: number; y: number }
/** Returns true when the tile at (tx, ty) is solid or out of bounds. */
export type TileBlocked = (tx: number, ty: number) => boolean;

export interface BfsResult {
  /** Tiles from the start (exclusive) to the destination (inclusive). Empty when start === goal. */
  path: GridPoint[];
  /** False when the goal was unreachable and `path` leads to the closest reached tile instead. */
  complete: boolean;
  /** Tiles expanded (for diagnostics/tests). */
  expanded: number;
}

/** 4-neighbour order is fixed so results are deterministic: up, right, down, left. */
const NEIGHBOURS: readonly GridPoint[] = [{ x: 0, y: -1 }, { x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 }];

/**
 * Breadth-first search on a tile grid. Starting tile is always allowed even if blocked (an actor
 * nudged into a wall must still be able to walk out). When the goal is unreachable or the node cap is
 * hit, the path to the reached tile with the smallest Manhattan distance to the goal is returned.
 */
export function bfsPath(blocked: TileBlocked, width: number, height: number, from: GridPoint, to: GridPoint, opts: { maxNodes?: number } = {}): BfsResult {
  const maxNodes = opts.maxNodes ?? 4096;
  const inBounds = (p: GridPoint) => p.x >= 0 && p.y >= 0 && p.x < width && p.y < height;
  if (!inBounds(from)) return { path: [], complete: false, expanded: 0 };
  if (from.x === to.x && from.y === to.y) return { path: [], complete: true, expanded: 0 };

  const key = (x: number, y: number) => y * width + x;
  const parent = new Int32Array(width * height).fill(-1);
  const visited = new Uint8Array(width * height);
  const queue: number[] = [key(from.x, from.y)];
  visited[key(from.x, from.y)] = 1;
  let head = 0;
  let expanded = 0;
  let best = key(from.x, from.y);
  let bestDist = Math.abs(from.x - to.x) + Math.abs(from.y - to.y);
  let found = -1;

  while (head < queue.length && expanded < maxNodes) {
    const cur = queue[head++] as number;
    expanded++;
    const cx = cur % width, cy = Math.floor(cur / width);
    const d = Math.abs(cx - to.x) + Math.abs(cy - to.y);
    if (d < bestDist) { bestDist = d; best = cur; }
    if (cx === to.x && cy === to.y) { found = cur; break; }
    for (const n of NEIGHBOURS) {
      const nx = cx + n.x, ny = cy + n.y;
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
      const k = key(nx, ny);
      if (visited[k]) continue;
      if (blocked(nx, ny)) continue;
      visited[k] = 1;
      parent[k] = cur;
      queue.push(k);
    }
  }

  const end = found >= 0 ? found : best;
  const path: GridPoint[] = [];
  let k = end;
  const startKey = key(from.x, from.y);
  while (k !== startKey && k >= 0) {
    path.push({ x: k % width, y: Math.floor(k / width) });
    k = parent[k] as number;
  }
  path.reverse();
  return { path, complete: found >= 0, expanded };
}

/** Removes intermediate points on straight runs so the actor turns only at corners. */
export function simplifyPath(path: readonly GridPoint[]): GridPoint[] {
  if (path.length < 3) return [...path];
  const out: GridPoint[] = [path[0] as GridPoint];
  for (let i = 1; i < path.length - 1; i++) {
    const a = out[out.length - 1] as GridPoint, b = path[i] as GridPoint, c = path[i + 1] as GridPoint;
    const collinear = (b.x - a.x) * (c.y - b.y) === (b.y - a.y) * (c.x - b.x);
    if (!collinear) out.push(b);
  }
  out.push(path[path.length - 1] as GridPoint);
  return out;
}

/**
 * Nearest walkable tile to `target` by ring search (deterministic scan order). Returns null when no
 * tile within `maxRadius` rings is free.
 */
export function nearestWalkableTile(blocked: TileBlocked, width: number, height: number, target: GridPoint, maxRadius = 6): GridPoint | null {
  const ok = (x: number, y: number) => x >= 0 && y >= 0 && x < width && y < height && !blocked(x, y);
  if (ok(target.x, target.y)) return { x: target.x, y: target.y };
  for (let r = 1; r <= maxRadius; r++) {
    let bestP: GridPoint | null = null;
    let bestD = Infinity;
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = target.x + dx, y = target.y + dy;
        if (!ok(x, y)) continue;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; bestP = { x, y }; }
      }
    }
    if (bestP) return bestP;
  }
  return null;
}

/**
 * Samples a segment every `stepPx` pixels (endpoints included) and returns true when `test` never
 * reports a blocked sample. Used for straight-line walking and path string-pulling.
 */
export function lineClear(x0: number, y0: number, x1: number, y1: number, stepPx: number, test: (x: number, y: number) => boolean): boolean {
  const dx = x1 - x0, dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  const n = Math.max(1, Math.ceil(dist / stepPx));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    if (test(x0 + dx * t, y0 + dy * t)) return false;
  }
  return true;
}

/** Greedy string-pulling: keeps only the waypoints needed so every hop is a clear straight line. */
export function pullString(points: readonly GridPoint[], clear: (a: GridPoint, b: GridPoint) => boolean): GridPoint[] {
  if (points.length <= 2) return [...points];
  const out: GridPoint[] = [points[0] as GridPoint];
  let anchor = 0;
  while (anchor < points.length - 1) {
    let far = anchor + 1;
    for (let j = points.length - 1; j > anchor + 1; j--) {
      if (clear(points[anchor] as GridPoint, points[j] as GridPoint)) { far = j; break; }
    }
    out.push(points[far] as GridPoint);
    anchor = far;
  }
  return out;
}
